import { Alert, App as AntApp, Button, Empty, Input, Skeleton } from "antd";
import axios from "axios";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BedIcon,
  BusIcon,
  CheckIcon,
  InfoIcon,
  MapPinIcon,
  MoonIcon,
  RouteIcon,
  SearchIcon,
  SparklesIcon,
  TentTreeIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  PortalTemplatePlan,
  PortalTemplateRecommendation,
  PortalTemplateRecommendationPlan,
  PortalTemplateSummary,
  useApplyPortalTemplatesMutation,
  usePortalTemplateRecommendationsQuery,
  usePortalTemplatesQuery,
} from "../actions/itinerary.actions";
import PortalFlowSteps from "./PortalFlowSteps";
import { PortalPageHeader } from "./PortalProduct";
import type { PortalRouteStop } from "./PortalRoutePlannerPage";

type SelectedTemplate = Pick<PortalTemplateSummary, "id" | "name" | "destinations" | "nights"> & {
  dayOffset?: number;
  matchedCity?: string;
  matchedNights?: number;
};
type DisplayTemplate = SelectedTemplate &
  Partial<
    Pick<PortalTemplateRecommendation, "hotelCount" | "activityCount" | "transportCount" | "dailyTransportCount">
  >;
type LengthMode = "exact" | "all";

/** An AI plan normalised for display; `picks` maps it onto route stops when a city route exists. */
type AiPlanView = {
  key: string;
  plan: PortalTemplateRecommendationPlan;
  picks: Record<number, SelectedTemplate> | null;
};

const roleContent: Record<PortalTemplateRecommendationPlan["role"], { label: string; description: string }> = {
  best_fit: { label: "Best fit", description: "AI matched this plan to your route and trip duration" },
  most_popular: { label: "Most popular", description: "AI balanced itinerary fit with frequent team usage" },
  simplest: { label: "Simplest plan", description: "AI found the fewest packages needed for this trip" },
  alternative: { label: "Another match", description: "A published package that also fits this route and duration" },
};

/** Stable identity of a recommended plan; several plans can share a role. */
const planKey = (plan: PortalTemplateRecommendationPlan) => plan.templates.map((template) => template.id).join("+");

const normalizeDestination = (value: string) => value.trim().toLocaleLowerCase();

/**
 * Whether a shared template covers a planned city. Only the template's city stops count (saved on the template,
 * or derived from its hotels by the server); a template whose city stops are unknown is never offered per city.
 */
const templateCoversStop = (template: PortalTemplateSummary, stop: PortalRouteStop) => {
  const city = normalizeDestination(stop.city || "");
  // Cities are compared on their own: the route's "country" can be a state or region, so it is not compared here;
  // the list is already limited to templates for the trip's destinations.
  return (template.cityStops || []).some((cityStop) => normalizeDestination(cityStop.city) === city);
};

const toSelected = (template: PortalTemplateSummary | PortalTemplateRecommendation): SelectedTemplate => ({
  id: template.id,
  name: template.name,
  destinations: template.destinations,
  nights: template.nights,
  dayOffset: "dayOffset" in template ? template.dayOffset : undefined,
  matchedCity: "matchedCity" in template ? template.matchedCity : undefined,
  matchedNights: "matchedNights" in template ? template.matchedNights : undefined,
});

const followsRouteOrder = (templates: SelectedTemplate[], route: string[]) => {
  const normalizedRoute = route.map(normalizeDestination);
  let lastEnd = 0;
  return templates.every((template, templateIndex) => {
    const templateRoute = template.destinations.map(normalizeDestination);
    const starts: number[] = [];
    for (let start = 0; start <= normalizedRoute.length - templateRoute.length; start += 1) {
      if (templateRoute.every((destination, offset) => destination === normalizedRoute[start + offset]))
        starts.push(start);
    }
    const validStart = starts.find((start) =>
      templateIndex === 0 ? start === 0 : start >= lastEnd && start <= lastEnd + 1,
    );
    if (validStart === undefined) return false;
    lastEnd = Math.max(lastEnd, validStart + templateRoute.length - 1);
    return true;
  });
};

const getApplyError = (error: unknown) =>
  axios.isAxiosError<{ error?: string }>(error)
    ? error.response?.data?.error || "Failed to apply package plan"
    : "Failed to apply package plan";

/** Returns the picks without the template chosen for one stop. */
const withoutPick = (picks: Record<number, SelectedTemplate>, index: number) =>
  Object.fromEntries(Object.entries(picks).filter(([key]) => Number(key) !== index));

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? "" : "s"}`;

/**
 * Full-page step that offers shared-template plans for a trip. With a city route it shows one exact-length
 * template list per stop (other lengths on request) and an AI plan that fills every stop in one click;
 * without one it falls back to a flat, ordered selection. Applies the result to the portal draft.
 */
const PortalTemplatePickerPage = ({
  open,
  onClose,
  destinations,
  nights,
  routeStops,
  onAdjustRoute,
  onApply,
}: {
  open: boolean;
  onClose: () => void;
  destinations: string[];
  nights: number;
  routeStops: PortalRouteStop[];
  onAdjustRoute: () => void;
  onApply: (plan: PortalTemplatePlan) => void;
}) => {
  const { message } = AntApp.useApp();
  const pageRef = useRef<HTMLDivElement>(null);
  const [picks, setPicks] = useState<Record<number, SelectedTemplate>>({});
  const [flatSelected, setFlatSelected] = useState<SelectedTemplate[]>([]);
  const [selectedPlanKey, setSelectedPlanKey] = useState<string | null>(null);
  const [activeStop, setActiveStop] = useState(0);
  const [lengthMode, setLengthMode] = useState<LengthMode>("exact");
  const [templateSearch, setTemplateSearch] = useState("");
  const [showAlternatives, setShowAlternatives] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPicks({});
    setFlatSelected([]);
    setSelectedPlanKey(null);
    setActiveStop(0);
    setLengthMode("exact");
    setTemplateSearch("");
    setShowAlternatives(false);
  }, [destinations, open, routeStops]);

  useEffect(() => {
    if (open) pageRef.current?.scrollIntoView({ block: "start" });
  }, [open]);

  const hasCityRoute =
    routeStops.length > 0 &&
    routeStops.every((stop) => stop.country && stop.city && Number(stop.nights) > 0) &&
    routeStops.reduce((sum, stop) => sum + Number(stop.nights), 0) === nights;
  const recommendationRoute = useMemo(
    () =>
      hasCityRoute
        ? routeStops.map((stop) => ({
            destination: stop.country,
            city: stop.city,
            nights: Number(stop.nights),
          }))
        : destinations.map((destination) => ({ destination })),
    [destinations, hasCityRoute, routeStops],
  );
  const routeDestinations = hasCityRoute ? routeStops.map((stop) => stop.city) : destinations;
  const recommendations = usePortalTemplateRecommendationsQuery(nights, recommendationRoute, open);
  const templatesQuery = usePortalTemplatesQuery();
  const applyTemplates = useApplyPortalTemplatesMutation();
  const templateList = useMemo(() => templatesQuery.data || [], [templatesQuery.data]);

  const stopRanges = useMemo(() => {
    let start = 0;
    return routeStops.map((stop) => {
      const range = { start, nights: Number(stop.nights) };
      start += Number(stop.nights);
      return range;
    });
  }, [routeStops]);

  /** Places each plan template on the stop that contains its day offset; plans that cannot be mapped one-to-one are skipped. */
  const planViews = useMemo<AiPlanView[]>(
    () =>
      (recommendations.data?.plans || []).flatMap<AiPlanView>((plan) => {
        const key = planKey(plan);
        if (!hasCityRoute) return [{ key, plan, picks: null }];
        const mapped: Record<number, SelectedTemplate> = {};
        for (const template of plan.templates) {
          const offset = template.dayOffset ?? 0;
          const stopIndex = stopRanges.findIndex(
            (range) => offset >= range.start && offset < range.start + range.nights,
          );
          if (stopIndex < 0 || mapped[stopIndex]) return [];
          mapped[stopIndex] = toSelected(template);
        }
        return [{ key, plan, picks: mapped }];
      }),
    [hasCityRoute, recommendations.data?.plans, stopRanges],
  );
  const featuredView = planViews.find((view) => view.plan.role === "best_fit") || planViews[0];
  const alternativeViews = planViews.filter((view) => view !== featuredView);
  const aiPickIds = useMemo(
    () => new Set(Object.values(featuredView?.picks || {}).map((template) => template.id)),
    [featuredView],
  );

  // Per-stop placement: each stop takes the longer of its own nights and its template's nights.
  const placement = useMemo(() => {
    let offset = 0;
    return routeStops.map((stop, index) => {
      const pick = picks[index];
      const stopNights = Number(stop.nights);
      const effectiveNights = Math.max(stopNights, pick ? Number(pick.nights) : 0);
      const start = offset;
      offset += effectiveNights;
      return {
        stop,
        pick,
        start,
        stopNights,
        covered: pick ? Math.min(stopNights, Number(pick.nights)) : 0,
        open: pick ? Math.max(0, stopNights - Number(pick.nights)) : stopNights,
        extra: pick ? Math.max(0, Number(pick.nights) - stopNights) : 0,
      };
    });
  }, [picks, routeStops]);
  const pickedCount = placement.filter((entry) => entry.pick).length;
  const cityApplicationNights = placement.reduce((sum, entry) => sum + entry.stopNights + entry.extra, 0);
  const cityCovered = placement.reduce((sum, entry) => sum + entry.covered + entry.extra, 0);
  const cityOpen = placement.reduce((sum, entry) => sum + entry.open, 0);

  const flatNights = flatSelected.reduce((sum, template) => sum + Number(template.nights), 0);
  const flatApplicationNights = Math.max(nights, flatNights);
  const flatFollowsRoute = followsRouteOrder(flatSelected, destinations);

  const applicationNights = hasCityRoute ? cityApplicationNights : flatApplicationNights;
  const extendsItinerary = applicationNights > nights;
  const selectedCount = hasCityRoute ? pickedCount : flatSelected.length;
  const canApply = selectedCount > 0 && applicationNights <= 30 && (hasCityRoute ? true : flatFollowsRoute);

  const filteredTemplates = useMemo(() => {
    const search = templateSearch.trim().toLocaleLowerCase();
    return templateList.filter((template) => {
      const coversTrip =
        destinations.length === 0 ||
        template.destinations.some((destination) =>
          destinations.some(
            (tripDestination) => normalizeDestination(destination) === normalizeDestination(tripDestination),
          ),
        );
      if (!coversTrip) return false;
      return (
        !search ||
        template.name.toLocaleLowerCase().includes(search) ||
        template.destinations.some((destination) => destination.toLocaleLowerCase().includes(search))
      );
    });
  }, [destinations, templateList, templateSearch]);

  const stopIndex = Math.min(activeStop, Math.max(routeStops.length - 1, 0));
  const stopTemplates = useMemo(() => {
    const stop = routeStops[stopIndex];
    if (!hasCityRoute || !stop) return { exact: [], other: [] };
    const stopNights = Number(stop.nights);
    const covering = filteredTemplates.filter((template) => templateCoversStop(template, stop));
    const rank = (template: PortalTemplateSummary) => (aiPickIds.has(template.id) ? 0 : 1);
    const byRank = (a: PortalTemplateSummary, b: PortalTemplateSummary) =>
      rank(a) - rank(b) || a.name.localeCompare(b.name);
    return {
      exact: covering.filter((template) => Number(template.nights) === stopNights).sort(byRank),
      other: covering
        .filter((template) => Number(template.nights) !== stopNights)
        .sort(
          (a, b) => Math.abs(Number(a.nights) - stopNights) - Math.abs(Number(b.nights) - stopNights) || byRank(a, b),
        ),
    };
  }, [aiPickIds, filteredTemplates, hasCityRoute, routeStops, stopIndex]);

  const resetSelection = () => {
    setPicks({});
    setFlatSelected([]);
    setSelectedPlanKey(null);
  };

  const applyAiPlan = (view: AiPlanView) => {
    if (view.picks) {
      setPicks(view.picks);
      setActiveStop(0);
    } else {
      setFlatSelected(view.plan.templates.map(toSelected));
    }
    setSelectedPlanKey(view.key);
  };

  const isAiPlanSelected = (view: AiPlanView) =>
    view.picks
      ? routeStops.every((_stop, index) => picks[index]?.id === view.picks?.[index]?.id) &&
        Object.keys(picks).length === Object.keys(view.picks).length
      : selectedPlanKey === view.key;

  /** Picks (or clears) the template for one route stop, then moves on to the next empty stop. */
  const toggleStopTemplate = (template: PortalTemplateSummary, index: number) => {
    if (picks[index]?.id === template.id) {
      setPicks((current) => withoutPick(current, index));
      setSelectedPlanKey(null);
      return;
    }
    const usedAt = Object.entries(picks).find(([key, pick]) => Number(key) !== index && pick.id === template.id);
    if (usedAt) {
      message.warning(
        `This package is already used for ${routeStops[Number(usedAt[0])]?.city}. Choose a different one.`,
      );
      return;
    }
    const next = { ...picks, [index]: toSelected(template) };
    setPicks(next);
    setSelectedPlanKey(null);
    const nextEmpty = routeStops.findIndex((_stop, stopPosition) => !next[stopPosition]);
    if (nextEmpty >= 0) {
      setActiveStop(nextEmpty);
      setLengthMode("exact");
      setTemplateSearch("");
    }
  };

  const toggleFlatTemplate = (template: PortalTemplateSummary) => {
    const isSelected = flatSelected.some((selected) => selected.id === template.id);
    if (!isSelected && flatSelected.length >= 5) {
      message.warning("A plan can contain at most five packages");
      return;
    }
    setFlatSelected((current) =>
      isSelected ? current.filter((selected) => selected.id !== template.id) : [...current, toSelected(template)],
    );
    setSelectedPlanKey("custom");
  };

  const moveFlatTemplate = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= flatSelected.length) return;
    const next = [...flatSelected];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setFlatSelected(next);
    setSelectedPlanKey("custom");
  };

  const closePage = () => {
    resetSelection();
    onClose();
  };

  const handleApply = async () => {
    if (!canApply) return;
    const positioned = placement.flatMap((entry) => (entry.pick ? [{ pick: entry.pick, start: entry.start }] : []));
    try {
      const plan = await applyTemplates.mutateAsync(
        hasCityRoute
          ? {
              templateIds: positioned.map((entry) => entry.pick.id),
              targetNights: applicationNights,
              dayOffsets: positioned.map((entry) => entry.start),
              destinations: Array.from(new Set(routeStops.map((stop) => stop.country))),
            }
          : { templateIds: flatSelected.map((template) => template.id) },
      );
      onApply(plan);
      message.success(
        extendsItinerary
          ? `Applied ${plural(selectedCount, "package")} and extended the trip to ${applicationNights} nights`
          : `Applied ${plural(selectedCount, "package")} to the draft`,
      );
      closePage();
    } catch (error) {
      console.error(error);
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        resetSelection();
        await Promise.all([templatesQuery.refetch(), recommendations.refetch()]);
      }
      message.error(getApplyError(error));
    }
  };

  if (!open) return null;

  const renderTemplateSequence = (items: DisplayTemplate[], compact = false) => (
    <ol
      className="overflow-hidden rounded-lg bg-[#F7F8FA] ring-1 ring-inset ring-gray-200"
      aria-label="Package sequence"
    >
      {items.map((template, index) => (
        <li
          key={template.id}
          className={`relative flex min-w-0 items-start gap-3 px-3.5 py-3 ${index > 0 ? "border-t border-gray-200" : ""}`}
        >
          <span
            className={`flex shrink-0 items-center justify-center rounded-md bg-(--brand-primary) font-semibold text-[var(--brand-primary-foreground)] ${compact ? "h-6 w-6 text-[11px]" : "h-7 w-7 text-xs"}`}
          >
            {index + 1}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-start justify-between gap-3">
              <h4
                className={`${compact ? "text-xs leading-[18px]" : "text-sm leading-5"} min-w-0 break-words font-semibold text-[#131B2A]`}
              >
                {template.name}
              </h4>
              <span className="flex shrink-0 items-center gap-1 rounded-md bg-white px-2 py-1 text-[11px] font-semibold tabular-nums text-[#344054] ring-1 ring-inset ring-gray-200">
                <MoonIcon className="h-3 w-3" />
                {template.matchedNights ?? template.nights}N
              </span>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] leading-4 text-gray-600">
              <span className="flex min-w-0 items-start gap-1">
                <MapPinIcon className="mt-0.5 h-3 w-3 shrink-0 text-gray-400" />
                <span className="break-words">{template.matchedCity || template.destinations.join(" → ")}</span>
              </span>
              {typeof template.hotelCount === "number" ? (
                <span className="flex items-center gap-1">
                  <BedIcon className="h-3 w-3 text-gray-400" />
                  {template.hotelCount}
                </span>
              ) : null}
              {typeof template.activityCount === "number" ? (
                <span className="flex items-center gap-1">
                  <TentTreeIcon className="h-3 w-3 text-gray-400" />
                  {template.activityCount}
                </span>
              ) : null}
              {typeof template.transportCount === "number" ? (
                <span className="flex items-center gap-1">
                  <BusIcon className="h-3 w-3 text-gray-400" />
                  {template.transportCount}
                </span>
              ) : null}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );

  /** One selectable template card. `forStop` is the route stop index in city mode, or undefined for the flat list. */
  const renderTemplateCard = (template: PortalTemplateSummary, forStop?: number) => {
    const stop = forStop === undefined ? undefined : routeStops[forStop];
    const stopNights = stop ? Number(stop.nights) : undefined;
    const flatIndex = flatSelected.findIndex((selected) => selected.id === template.id);
    const isSelected = forStop === undefined ? flatIndex >= 0 : picks[forStop]?.id === template.id;
    const difference = stopNights === undefined ? 0 : Number(template.nights) - stopNights;
    return (
      <button
        key={template.id}
        type="button"
        aria-pressed={isSelected}
        onClick={() => (forStop === undefined ? toggleFlatTemplate(template) : toggleStopTemplate(template, forStop))}
        className={`flex flex-col rounded-xl border bg-white p-4 text-left transition-colors ${isSelected ? "border-(--brand-secondary) ring-1 ring-(--brand-secondary)" : "border-gray-200 hover:border-(--brand-secondary)/60"}`}
      >
        <div className="flex min-h-5 flex-wrap items-center gap-1.5">
          {stop && aiPickIds.has(template.id) && difference === 0 ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-(--brand-primary) px-2 py-0.5 text-[11px] font-semibold text-[var(--brand-primary-foreground)]">
              <SparklesIcon className="h-3 w-3" /> AI pick
            </span>
          ) : null}
          {stop ? (
            difference === 0 ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                <CheckIcon className="h-3 w-3" /> Exact {stopNights}N
              </span>
            ) : difference > 0 ? (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                Adds {plural(difference, "night")} to the trip
              </span>
            ) : (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                {plural(-difference, "night")} left open
              </span>
            )
          ) : null}
        </div>
        <div className="mt-2 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="break-words text-sm font-semibold leading-5 text-[#131B2A]">{template.name}</h3>
            <p className="mt-1 flex items-start gap-1 break-words text-xs leading-4 text-gray-500">
              <MapPinIcon className="mt-0.5 h-3 w-3 shrink-0" />
              {template.destinations.join(" → ")}
            </p>
          </div>
          {isSelected ? (
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-(--brand-secondary) text-xs font-semibold text-white">
              {forStop === undefined ? flatIndex + 1 : <CheckIcon className="h-3.5 w-3.5" />}
            </span>
          ) : null}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-3 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <MoonIcon className="h-3.5 w-3.5" />
            {template.nights} nights
          </span>
          <span className="flex items-center gap-1" title="Hotels">
            <BedIcon className="h-3.5 w-3.5" />
            {template.hotelCount}
          </span>
          <span className="flex items-center gap-1" title="Activities">
            <TentTreeIcon className="h-3.5 w-3.5" />
            {template.activityCount}
          </span>
          <span className="flex items-center gap-1" title="Transfers">
            <BusIcon className="h-3.5 w-3.5" />
            {template.transportCount}
          </span>
        </div>
      </button>
    );
  };

  const renderAiSection = () => (
    <section aria-label="AI-suggested package plans">
      {recommendations.isLoading ? (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <Skeleton active paragraph={{ rows: 2 }} />
        </div>
      ) : recommendations.isError ? (
        <Alert
          type="error"
          showIcon
          message="AI recommendations are unavailable"
          description="You can retry or choose from the shared package library below."
          action={
            <Button size="small" onClick={() => recommendations.refetch()}>
              Retry
            </Button>
          }
        />
      ) : featuredView ? (
        <div className="overflow-hidden rounded-xl bg-(--brand-secondary) text-white">
          <div className="flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-(--brand-primary) px-2.5 py-1 text-[11px] font-semibold text-[var(--brand-primary-foreground)]">
                  <SparklesIcon className="h-3.5 w-3.5" /> AI first choice
                </span>
                {featuredView.plan.unfilledNights === 0 ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-300">
                    <CheckIcon className="h-3.5 w-3.5" /> Exact duration
                  </span>
                ) : (
                  <span className="text-xs font-medium text-amber-200">
                    {plural(featuredView.plan.unfilledNights, "night")} open
                  </span>
                )}
              </div>
              <h2 className="mt-2 text-lg font-semibold">{roleContent[featuredView.plan.role].label}</h2>
              <p className="mt-0.5 text-xs leading-5 text-gray-300">
                {roleContent[featuredView.plan.role].description}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              {alternativeViews.length > 0 ? (
                <Button
                  ghost
                  aria-expanded={showAlternatives}
                  onClick={() => setShowAlternatives((current) => !current)}
                  className="!border-white/30 !text-white"
                >
                  {showAlternatives ? "Hide" : "See"} {alternativeViews.length} other AI plan
                  {alternativeViews.length === 1 ? "" : "s"}
                </Button>
              ) : null}
              <Button
                icon={
                  isAiPlanSelected(featuredView) ? (
                    <CheckIcon className="h-4 w-4" />
                  ) : (
                    <SparklesIcon className="h-4 w-4" />
                  )
                }
                onClick={() => applyAiPlan(featuredView)}
                className="!border-white !bg-white !font-semibold !text-(--brand-secondary)"
              >
                {isAiPlanSelected(featuredView) ? "Plan selected" : "Use AI plan"}
              </Button>
            </div>
          </div>
          <div className="space-y-3 bg-white px-5 py-4 text-[#131B2A]">
            {hasCityRoute && recommendations.data?.missingDestinations?.length ? (
              <Alert
                type="info"
                showIcon
                message="Some route nights will remain empty"
                description={`No shared package matches ${recommendations.data.missingDestinations.join(", ")}. You can pick a package for the other stops and build these by hand.`}
              />
            ) : null}
            {renderTemplateSequence(featuredView.plan.templates)}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2" aria-label="Why AI suggested this plan">
              <span className="text-xs font-semibold text-[#344054]">Why this plan:</span>
              {featuredView.plan.reasons.map((reason) => (
                <span key={reason.code} className="inline-flex items-center gap-1 text-xs font-medium text-gray-600">
                  <CheckIcon className="h-3.5 w-3.5 text-emerald-600" />
                  {reason.label}
                </span>
              ))}
            </div>
          </div>
          {showAlternatives && alternativeViews.length > 0 ? (
            <div className="grid gap-3 border-t border-white/15 px-5 py-4 lg:grid-cols-2">
              {alternativeViews.map((view) => {
                const isSelected = isAiPlanSelected(view);
                return (
                  <article key={view.key} className="rounded-xl bg-white p-4 text-[#131B2A]">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-sm font-semibold">{roleContent[view.plan.role].label}</h3>
                          <span
                            className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${view.plan.unfilledNights === 0 ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}
                          >
                            {view.plan.unfilledNights === 0
                              ? `${view.plan.totalNights}/${nights} nights`
                              : `${plural(view.plan.unfilledNights, "night")} open`}
                          </span>
                        </div>
                        <p className="mt-1 text-xs leading-5 text-gray-500">
                          {roleContent[view.plan.role].description}
                        </p>
                      </div>
                      <Button
                        size="small"
                        icon={isSelected ? <CheckIcon className="h-3.5 w-3.5" /> : undefined}
                        onClick={() => applyAiPlan(view)}
                        className={
                          isSelected
                            ? "!border-(--brand-secondary) !bg-(--brand-secondary) !text-white"
                            : "!font-semibold"
                        }
                      >
                        {isSelected ? "Selected" : "Use plan"}
                      </Button>
                    </div>
                    <div className="mt-3">{renderTemplateSequence(view.plan.templates, true)}</div>
                  </article>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              recommendations.data?.missingDestinations?.length
                ? `No suitable shared packages cover ${recommendations.data.missingDestinations.join(", ")}.`
                : "AI has no complete plan for this route. Pick a package for each stop below."
            }
          />
        </div>
      )}
    </section>
  );

  const renderCityBrowser = () => {
    const stop = routeStops[stopIndex];
    const stopNights = Number(stop.nights);
    const visibleTemplates =
      lengthMode === "exact" ? stopTemplates.exact : [...stopTemplates.exact, ...stopTemplates.other];
    return (
      <section aria-label="Choose a package for each stop">
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Route stops">
          {routeStops.map((routeStop, index) => {
            const isActive = index === stopIndex;
            const pick = picks[index];
            const exactCount = filteredTemplates.filter(
              (template) =>
                templateCoversStop(template, routeStop) && Number(template.nights) === Number(routeStop.nights),
            ).length;
            return (
              <button
                key={`${routeStop.country}-${routeStop.city}-${index}`}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => {
                  setActiveStop(index);
                  setLengthMode("exact");
                }}
                className={`min-w-[180px] flex-1 rounded-xl border bg-white p-3 text-left transition-colors ${isActive ? "border-(--brand-secondary) ring-1 ring-(--brand-secondary)" : "border-gray-200 hover:border-gray-400"}`}
              >
                <span className="flex items-center justify-between gap-2 text-sm font-semibold">
                  <span className="flex items-center gap-2">
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-md text-[11px] ${pick ? "bg-emerald-50 text-emerald-700" : isActive ? "bg-(--brand-secondary) text-white" : "bg-gray-100 text-gray-500"}`}
                    >
                      {pick ? <CheckIcon className="h-3 w-3" /> : index + 1}
                    </span>
                    {routeStop.city}
                  </span>
                  <span className="flex items-center gap-1 rounded-md bg-gray-100 px-1.5 py-0.5 text-[11px] font-semibold text-gray-600">
                    <MoonIcon className="h-3 w-3" />
                    {routeStop.nights}N
                  </span>
                </span>
                <span className={`mt-1 block text-xs ${pick ? "font-semibold text-emerald-700" : "text-gray-500"}`}>
                  {pick ? "Package chosen" : `${plural(exactCount, "exact package")}`}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="portal-title text-xl font-semibold text-[#131B2A]">
            {stop.city}
            <span className="ml-2 text-xs font-normal text-gray-500">
              {plural(stopNights, "night")} · {plural(stopTemplates.exact.length, "exact match")}
            </span>
          </h2>
        </div>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg bg-gray-100 p-0.5" role="group" aria-label="Which packages to show">
            {(["exact", "all"] as LengthMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                aria-pressed={lengthMode === mode}
                onClick={() => setLengthMode(mode)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${lengthMode === mode ? "bg-white text-[#131B2A] shadow-sm" : "text-gray-500 hover:text-[#131B2A]"}`}
              >
                {mode === "exact"
                  ? `Exact ${stopNights}N (${stopTemplates.exact.length})`
                  : `All lengths (${stopTemplates.exact.length + stopTemplates.other.length})`}
              </button>
            ))}
          </div>
          <Input
            allowClear
            value={templateSearch}
            onChange={(event) => setTemplateSearch(event.target.value)}
            aria-label="Search packages"
            placeholder="Search packages or destinations"
            prefix={<SearchIcon className="h-4 w-4 text-gray-400" />}
            className="max-w-sm"
          />
        </div>
        {lengthMode === "all" ? (
          <p className="mb-3 flex items-start gap-2 rounded-lg bg-gray-100 px-3 py-2 text-xs text-gray-600">
            <InfoIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Templates that are not {stopNights} nights change the plan. Longer ones add nights to the trip. Shorter ones
            leave nights open for you to fill by hand.
          </p>
        ) : null}
        {visibleTemplates.length > 0 ? (
          <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
            {visibleTemplates.map((template) => renderTemplateCard(template, stopIndex))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
            No {stopNights}N templates for {stop.city}
            {templateSearch.trim() ? ` match "${templateSearch.trim()}"` : " yet"}.
            {stopTemplates.other.length > 0 && lengthMode === "exact" ? (
              <div className="mt-3">
                <Button size="small" onClick={() => setLengthMode("all")}>
                  Show other lengths
                </Button>
              </div>
            ) : (
              <p className="mt-1 text-xs">You can skip this stop and build it by hand.</p>
            )}
          </div>
        )}
        {lengthMode === "exact" && stopTemplates.exact.length > 0 && stopTemplates.other.length > 0 ? (
          <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-gray-500">
            <span>
              {stopTemplates.other.length} more {stop.city} template
              {stopTemplates.other.length === 1 ? " has" : "s have"} a different length.
            </span>
            <Button size="small" onClick={() => setLengthMode("all")}>
              Show other lengths
            </Button>
          </div>
        ) : null}
      </section>
    );
  };

  const renderFlatBrowser = () => (
    <section aria-label="Shared package library">
      <Input
        allowClear
        value={templateSearch}
        onChange={(event) => setTemplateSearch(event.target.value)}
        aria-label="Search packages"
        placeholder="Search packages or destinations"
        prefix={<SearchIcon className="h-4 w-4 text-gray-400" />}
        className="mb-4 max-w-sm"
      />
      <p className="mb-4 flex items-start gap-2 rounded-lg bg-gray-100 px-3 py-2 text-xs text-gray-600">
        <InfoIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Plan city stops to see templates stop by stop. Templates chosen here are applied in your chosen order.
      </p>
      {templatesQuery.isLoading ? (
        <Skeleton active />
      ) : templatesQuery.isError ? (
        <Alert
          type="error"
          showIcon
          message="Unable to load shared packages"
          action={
            <Button size="small" onClick={() => templatesQuery.refetch()}>
              Retry
            </Button>
          }
        />
      ) : filteredTemplates.length > 0 ? (
        <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
          {filteredTemplates.map((template) => renderTemplateCard(template))}
        </div>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No shared packages match this trip" />
      )}
    </section>
  );

  const planStatus = !selectedCount
    ? "Choose a package for each stop, or use the AI plan."
    : extendsItinerary
      ? `The trip will grow to ${applicationNights} nights.`
      : hasCityRoute && cityOpen > 0
        ? `${plural(cityOpen, "night")} stay empty so you can build them by hand.`
        : "Every night is covered. You can edit anything after applying.";

  const renderPlanPanel = () => (
    <aside
      aria-label="Your trip plan"
      className="rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(19,27,42,0.04)] lg:sticky lg:top-4"
    >
      <h2 className="text-sm font-semibold text-[#131B2A]">Your trip plan</h2>
      <p className="mt-0.5 text-xs text-gray-500">
        {routeDestinations.join(" → ") || "Choose a destination"} ·{" "}
        {plural(Math.max(nights, applicationNights), "night")}
      </p>
      {hasCityRoute ? (
        <>
          <div className="mt-3 flex items-center justify-between text-xs font-semibold tabular-nums">
            <span>
              {cityCovered} of {applicationNights} nights covered
            </span>
            <span className={cityOpen === 0 && pickedCount > 0 ? "text-emerald-700" : "text-gray-500"}>
              {cityOpen > 0 ? `${cityOpen} open` : pickedCount > 0 ? "Complete" : ""}
            </span>
          </div>
          <div
            className="mt-2 flex h-2 gap-0.5"
            role="img"
            aria-label={`${cityCovered} of ${applicationNights} nights covered`}
          >
            {placement.map((entry, index) => (
              <span
                key={`${entry.stop.city}-${index}`}
                style={{ flex: entry.stopNights + entry.extra }}
                className={`rounded-full ${!entry.pick ? "bg-gray-200" : entry.open > 0 ? "bg-amber-400" : "bg-emerald-600"}`}
              />
            ))}
          </div>
          <ol className="mt-4 space-y-2">
            {placement.map((entry, index) => (
              <li
                key={`${entry.stop.city}-${index}`}
                className={`rounded-lg border p-2.5 ${entry.pick ? "border-gray-200" : "border-dashed border-gray-300"}`}
              >
                <div className="flex items-center justify-between gap-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-500">
                  <span>
                    {index + 1}. {entry.stop.city} · {entry.stopNights}N
                  </span>
                  {entry.pick ? (
                    <button
                      type="button"
                      className="font-semibold normal-case tracking-normal underline underline-offset-2"
                      aria-label={`Remove package from ${entry.stop.city}`}
                      onClick={() => {
                        setPicks((current) => withoutPick(current, index));
                        setSelectedPlanKey(null);
                      }}
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
                {entry.pick ? (
                  <>
                    <p className="mt-1 break-words text-xs font-semibold text-[#131B2A]">{entry.pick.name}</p>
                    <p
                      className={`mt-0.5 text-[11px] font-medium ${entry.extra > 0 || entry.open > 0 ? "text-amber-700" : "text-emerald-700"}`}
                    >
                      {entry.extra > 0
                        ? `Adds ${plural(entry.extra, "night")} to the trip`
                        : entry.open > 0
                          ? `${plural(entry.open, "night")} left open`
                          : `Fills all ${entry.stopNights} nights`}
                    </p>
                  </>
                ) : (
                  <button
                    type="button"
                    className="mt-1 text-left text-xs text-gray-500 underline underline-offset-2"
                    onClick={() => {
                      setActiveStop(index);
                      setLengthMode("exact");
                    }}
                  >
                    No template yet. Choose for {entry.stop.city}
                  </button>
                )}
              </li>
            ))}
          </ol>
        </>
      ) : (
        <div className="mt-3">
          <p className="mb-2 text-xs font-semibold tabular-nums text-gray-600">
            {flatSelected.length
              ? `${plural(flatSelected.length, "package")} · ${flatNights}/${nights} nights`
              : "No packages yet"}
          </p>
          <ol className="space-y-2" aria-label="Selected packages">
            {flatSelected.map((template, index) => (
              <li key={template.id} className="flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-2">
                <span className="min-w-0 flex-1 break-words text-xs font-medium leading-4">
                  {index + 1}. {template.name}
                </span>
                <Button
                  type="text"
                  size="small"
                  disabled={index === 0}
                  icon={<ArrowUpIcon className="h-3 w-3" />}
                  onClick={() => moveFlatTemplate(index, index - 1)}
                  aria-label={`Move ${template.name} earlier`}
                />
                <Button
                  type="text"
                  size="small"
                  disabled={index === flatSelected.length - 1}
                  icon={<ArrowDownIcon className="h-3 w-3" />}
                  onClick={() => moveFlatTemplate(index, index + 1)}
                  aria-label={`Move ${template.name} later`}
                />
              </li>
            ))}
          </ol>
          {flatSelected.length > 0 && !flatFollowsRoute ? (
            <p className="mt-2 text-xs font-medium text-red-600">
              Reorder the templates so they follow the destination route.
            </p>
          ) : null}
        </div>
      )}
      {applicationNights > 30 ? (
        <p className="mt-3 text-xs font-medium text-red-600">This selection exceeds the 30-night itinerary limit.</p>
      ) : null}
      <div className="mt-4 grid gap-2">
        <Button
          type="primary"
          size="large"
          disabled={!canApply}
          loading={applyTemplates.isPending}
          onClick={handleApply}
          className="!border-(--brand-secondary) !bg-(--brand-secondary) !font-semibold !text-white hover:!bg-(--brand-secondary-hover)"
        >
          {extendsItinerary ? "Apply and extend trip" : "Apply to itinerary"}
        </Button>
        <Button size="large" onClick={closePage}>
          Skip, build manually
        </Button>
      </div>
      <p className="mt-3 text-xs leading-5 text-gray-500">
        {planStatus} Hotels, activities and transfers from the chosen templates are copied into the daily plan.
      </p>
    </aside>
  );

  return (
    <div ref={pageRef} className="scroll-mt-4 space-y-4">
      <div className="space-y-3">
        <PortalFlowSteps current={3} />
        <PortalPageHeader
          title="Choose packages for your trip"
          description="AI matched packages to your route, trip duration, and team usage."
          meta={`${routeDestinations.join(" → ") || "Choose a destination"} · ${hasCityRoute ? "City route" : "Destination-only route"} · ${plural(nights, "night")}`}
          actions={
            <Button
              icon={<RouteIcon className="h-4 w-4" />}
              onClick={onAdjustRoute}
              className="!border-(--brand-primary)/60 !bg-[var(--brand-primary-soft)] !font-semibold !text-(--brand-primary)"
            >
              {hasCityRoute ? "Adjust city stops" : "Plan city stops"}
            </Button>
          }
        />
      </div>
      {renderAiSection()}
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
        <div className="min-w-0">{hasCityRoute ? renderCityBrowser() : renderFlatBrowser()}</div>
        {renderPlanPanel()}
      </div>
    </div>
  );
};

export default PortalTemplatePickerPage;
